/**
 * @fileoverview WebAuthn Sign-In Service – Passwordless passkey authentication
 *
 * Orchestrates the two-step passkey sign-in flow:
 *  1. `initiatePasskeySignIn(email)` – calls `webauthn-sign-in-options` to get
 *     a WebAuthn challenge without needing an existing Supabase session.
 *  2. `verifyPasskeySignIn(authResponse, email)` – calls
 *     `webauthn-sign-in-verify` and then exchanges the returned `token_hash`
 *     for a live Supabase session via `supabase.auth.verifyOtp`.
 *
 * This is separate from the existing encryption-unlock WebAuthn flow
 * (`WebAuthnService.ts`) which operates on already-authenticated users.
 *
 * @module services/WebAuthnSignInService
 */

import {
  startAuthentication,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { supabase } from '@xanectech/supabase-client';
import { createLogger, isRecord } from '@xanectech/lib';

const logger = createLogger('WebAuthnSignInService');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PasskeySignInResult {
  success: boolean;
  /** Populated on success with the master-key material (if the edge function
   *  returned it).  Callers may use this to initialise the encryption session
   *  in the same round-trip as sign-in. */
  wrappedKey?: {
    encryptedMasterKey: string;
    keyWrapAlgorithm: string;
    keyWrapIv: string;
    format?: string;
  };
  error?: string;
}

interface SignInOptionsResponse {
  success: boolean;
  options: PublicKeyCredentialRequestOptionsJSON;
}

interface SignInVerifyResponse {
  success: boolean;
  token_hash: string;
  email: string;
  wrappedKey?: PasskeySignInResult['wrappedKey'];
  error?: string;
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

const isSignInOptionsResponse = (v: unknown): v is SignInOptionsResponse =>
  isRecord(v) && v.success === true && isRecord(v.options);

const isSignInVerifyResponse = (v: unknown): v is SignInVerifyResponse =>
  isRecord(v) &&
  v.success === true &&
  typeof v.token_hash === 'string' &&
  typeof v.email === 'string';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Perform a full passkey sign-in for `email`.
 *
 * Triggers the browser's WebAuthn prompt. Returns a `PasskeySignInResult`
 * with `success: true` if the user was signed in, or `success: false` with an
 * error message otherwise.
 *
 * After a successful call the Supabase client will have an active session (set
 * via `supabase.auth.verifyOtp`).
 */
export async function performPasskeySignIn(
  email: string,
): Promise<PasskeySignInResult> {
  // Step 1 – request authentication options
  let optionsData: SignInOptionsResponse;
  try {
    const { data, error } = await supabase.functions.invoke(
      'webauthn-sign-in-options',
      { body: { email } },
    );
    if (error) {
      return {
        success: false,
        error: isRecord(error) && typeof error.message === 'string'
          ? (error.message as string)
          : 'Failed to start passkey sign-in',
      };
    }
    if (!isSignInOptionsResponse(data)) {
      return {
        success: false,
        error: 'Invalid response from sign-in options endpoint',
      };
    }
    optionsData = data;
  } catch (err) {
    logger.error('Failed to fetch passkey sign-in options', err as Error);
    return {
      success: false,
      error: 'Unable to start passkey sign-in. Please try again.',
    };
  }

  // Step 2 – prompt the user for biometric / passkey
  let authResponse: AuthenticationResponseJSON;
  try {
    authResponse = await startAuthentication({ optionsJSON: optionsData.options });
  } catch (err: unknown) {
    const name =
      isRecord(err) && typeof err.name === 'string' ? err.name : '';
    if (name === 'NotAllowedError') {
      return {
        success: false,
        error: 'Passkey authentication was cancelled. Please try again.',
      };
    }
    logger.error('WebAuthn sign-in prompt failed', err as Error);
    return {
      success: false,
      error: 'Passkey authentication failed. Please try password instead.',
    };
  }

  // Step 3 – verify on the server and obtain a one-time token
  let verifyData: SignInVerifyResponse;
  try {
    const { data, error } = await supabase.functions.invoke(
      'webauthn-sign-in-verify',
      { body: { authenticationResponse: authResponse } },
    );
    if (error) {
      return {
        success: false,
        error: isRecord(error) && typeof error.message === 'string'
          ? (error.message as string)
          : 'Passkey verification failed',
      };
    }
    if (!isSignInVerifyResponse(data)) {
      return {
        success: false,
        error: 'Invalid response from sign-in verify endpoint',
      };
    }
    verifyData = data;
  } catch (err) {
    logger.error('Failed to verify passkey sign-in', err as Error);
    return {
      success: false,
      error: 'Passkey verification failed. Please try again.',
    };
  }

  // Step 4 – exchange token_hash for an active Supabase session
  const { error: otpError } = await supabase.auth.verifyOtp({
    token_hash: verifyData.token_hash,
    type: 'magiclink',
  });

  if (otpError) {
    logger.error('Failed to establish session after passkey sign-in', {
      error: otpError.message,
    });
    return {
      success: false,
      error: 'Failed to establish session. Please try again.',
    };
  }

  logger.info('Passkey sign-in successful');

  return {
    success: true,
    wrappedKey: verifyData.wrappedKey,
  };
}
